import { useState } from "react";
import { Alert } from "../../components/ui/Alert";
import { Button } from "../../components/ui/Button";
import { Modal } from "../../components/ui/Modal";
import { Spinner } from "../../components/ui/Spinner";
import type { User } from "../../hooks/api/useUsers";
import {
  useAdminUserFutureReservations,
  useBulkCancelUserFutureReservations,
} from "../../hooks/api/useUsers";
import { useToastStore } from "../../stores/toastStore";

interface UserFutureReservationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetUser: User | null;
}

export function UserFutureReservationsModal({
  isOpen,
  onClose,
  targetUser,
}: UserFutureReservationsModalProps) {
  const { data, isLoading } = useAdminUserFutureReservations(targetUser?.email);
  const bulkCancel = useBulkCancelUserFutureReservations();
  const addToast = useToastStore((s) => s.addToast);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleBulkCancel = async () => {
    if (!targetUser) {
      return;
    }
    setError(null);

    try {
      const result = await bulkCancel.mutateAsync(targetUser.email);
      const parts: string[] = [];
      if (result.deletedReservations > 0) {
        parts.push(`予約${result.deletedReservations}件`);
      }
      if (result.deletedRepeats > 0) {
        parts.push(`繰り返しルール${result.deletedRepeats}件`);
      }
      const summary = parts.length > 0 ? parts.join("、") : "0件";

      const hasFailures =
        result.failedReservations > 0 || result.failedRepeats > 0;
      if (hasFailures) {
        const failParts: string[] = [];
        if (result.failedReservations > 0) {
          failParts.push(`予約${result.failedReservations}件`);
        }
        if (result.failedRepeats > 0) {
          failParts.push(`ルール${result.failedRepeats}件`);
        }
        setError(
          `${summary}をキャンセルしましたが、${failParts.join("、")}の削除に失敗しました`
        );
        addToast(`${targetUser.name}の一部キャンセルに失敗しました`, "error");
        return;
      }

      addToast(`${targetUser.name}の${summary}をキャンセルしました`);
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "一括キャンセルに失敗しました"
      );
    }
  };

  const reservations = data?.reservations ?? [];
  const repeats = data?.repeats ?? [];
  const hasData = reservations.length > 0 || repeats.length > 0;
  const targetName = targetUser?.name ?? "";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`${targetName}の全予約キャンセル`}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={bulkCancel.isPending}
          >
            閉じる
          </Button>
          {hasData && !isLoading && (
            <Button
              variant="danger"
              onClick={handleBulkCancel}
              isLoading={bulkCancel.isPending}
              aria-label={`${targetName}の全予約をキャンセル`}
            >
              全予約をキャンセル
            </Button>
          )}
        </>
      }
    >
      {isLoading && (
        <div
          className="flex items-center justify-center py-8"
          role="status"
          aria-label="読み込み中"
        >
          <Spinner size="lg" />
        </div>
      )}

      {!isLoading && hasData && (
        <div className="space-y-2">
          <p className="text-gray-600">
            {targetName}の全予約をキャンセルしますか？
          </p>
          <p className="text-sm text-gray-500">
            {[
              reservations.length > 0 && `予約${reservations.length}件`,
              repeats.length > 0 && `繰り返しルール${repeats.length}件`,
            ]
              .filter(Boolean)
              .join("、")}
          </p>
        </div>
      )}

      {!(isLoading || hasData) && (
        <p className="py-8 text-center text-sm text-gray-500">
          {targetName}の予約はありません
        </p>
      )}

      {error && (
        <Alert variant="error" className="mt-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </Modal>
  );
}
